import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { PASSWORD_LENGTH } from 'config'

import { Input } from 'components'
import Button from 'components/Button'
import ConfirmBoxBasic from 'components/ConfirmBoxBasic'

import '../style.scss'

type Props = {
  isLoading?: boolean
  onNext: (password: string, closeDialog: () => void) => void
}

export const SetPasswordForm = ({ isLoading, onNext }: Props) => {
  const { t } = useTranslation()
  const [isActiveConfirm, setIsActiveConfirm] = useState(false)

  const passwordLengthValidateText = t('common.validation.password_length', {
    min: PASSWORD_LENGTH.MIN,
    max: PASSWORD_LENGTH.MAX,
  })

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object().shape({
      password: Yup.string()
        .min(PASSWORD_LENGTH.MIN, passwordLengthValidateText)
        .max(PASSWORD_LENGTH.MAX, passwordLengthValidateText)
        .required(t('common.validation.required')),
      confirmPassword: Yup.string()
        .oneOf(
          [Yup.ref('password'), null],
          t('common.validation.password_must_be_the_same')
        )
        .required(t('common.validation.required')),
    }),
    onSubmit: () => {
      setIsActiveConfirm(true)
    },
  })

  return (
    <>
      <form className="UserSetting__form" onSubmit={formik.handleSubmit}>
        <Input
          className="UserSetting__form__input"
          label={t('user_setting.set_password_form.create_password')}
          placeholder={t('login_form.password.placeholder')}
          type="password"
          name="password"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.password}
          error={formik.touched.password && formik.errors.password}
          minLength={PASSWORD_LENGTH.MIN}
          maxLength={PASSWORD_LENGTH.MAX}
        />

        <Input
          className="UserSetting__form__input"
          placeholder={t('user_setting.set_password_form.confirm_password')}
          name="confirmPassword"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.confirmPassword}
          error={
            formik.touched.confirmPassword && formik.errors.confirmPassword
          }
          type="password"
          minLength={PASSWORD_LENGTH.MIN}
          maxLength={PASSWORD_LENGTH.MAX}
        />

        <span className="UserSetting__span">{passwordLengthValidateText}</span>

        <Button
          className="UserSetting__form__submit"
          size="medium"
          type="submit"
        >
          {t('common.save')}
        </Button>
      </form>

      <ConfirmBoxBasic
        isActive={isActiveConfirm}
        title={t('user_setting.update_account_confirm_dialog_message')}
        loading={isLoading}
        onClose={() => setIsActiveConfirm(false)}
        onCancel={() => setIsActiveConfirm(false)}
        onOk={() => {
          onNext(formik.values.password, () => setIsActiveConfirm(false))
        }}
      />
    </>
  )
}
